import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MarketingCampaignsHistoryService } from '../../../../services/marketing-campaigns/marketing-campaigns-history.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MarketingCampaignsService } from 'src/app/services/marketing-campaigns/marketing-campaigns.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { PaginatorComponent } from 'src/app/components/paginator/paginator.component';
import { ObjectViewerComponent, ObjectViewerDialogModel } from 'src/app/components/object-viewer/object-viewer.component';
import {
  MarketingCampaignAnalytics,
  MarketingCampaignAnalyticsTotals,
  SentEmailFilterInterface,
  SentEmailInterface
} from 'src/app/interfaces/campaigns/history/history.interface';

interface MarketingCampaignHistoryRow {
  campaign: { name?: string; id?: string };
  target: string;
  channel?: string;
  sentAt: Date | null;
  clickCount?: number;
  status: string;
  error?: string;
  history: SentEmailInterface;
}

const HISTORY_STATUS_FILTERS = [
  'queued',
  'sending',
  'sent',
  'delivered',
  'failed',
  'undelivered',
  'bounced',
  'dropped',
  'spam',
  'unsubscribed',
  'opened',
  'clicked',
];

@Component({
  selector: 'app-marketing-campaigns-history',
  templateUrl: './marketing-campaigns-history.component.html',
  styleUrls: ['./marketing-campaigns-history.component.scss']
})
export class MarketingCampaignsHistoryComponent implements OnInit {
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;
  @ViewChild('historySort') set historySort(sort: MatSort) {
    this.tableDataSource.sort = sort;
  }
  @ViewChild('analyticsSort') set analyticsSort(sort: MatSort) {
    this.analyticsDataSource.sort = sort;
  }

  public reload = false;
  public page = 1;
  public limit = 50;
  public filter = '';
  public status = 'all';
  public campaignId = 'all';
  public channel: 'all' | 'email' | 'sms' = 'all';
  public itemCount = 0;
  public analytics: MarketingCampaignAnalytics[] = [];
  public analyticsError = '';
  public analyticsTotals: MarketingCampaignAnalyticsTotals = this.emptyAnalyticsTotals();

  analyticsColumnNames = [
    'campaignName',
    'channel',
    'initialTimestamp',
    'sentCount',
    'deliveredCount',
    'failedCount',
    'deliveryRate',
    'uniqueClickedCount',
    'totalClickCount',
    'uniqueClickRate'
  ];
  columnNames = ['campaignName', 'target', 'channel', 'sentAt', 'clickCount', 'status', 'actionmenu'];
  analyticsDataSource = new MatTableDataSource<MarketingCampaignAnalytics>();
  tableDataSource = new MatTableDataSource<MarketingCampaignHistoryRow>();

  filterValues = HISTORY_STATUS_FILTERS;
  campaigns: { id: string, name: string }[] = [];
  searchForm: FormControl = new FormControl('');

  constructor(
    private marketingCampaignsHistoryService: MarketingCampaignsHistoryService,
    private campaignService: MarketingCampaignsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public utilService: UtilsService
  ) {
    this.analyticsDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'campaignName':
          return this.campaigns.find(campaign => campaign.id === item.campaignId)?.name || '';
        case 'initialTimestamp':
          return new Date(item.initialTimestamp).getTime();
        default:
          return (item as Record<string, any>)[property] ?? '';
      }
    };
    this.tableDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'campaignName':
          return item.campaign?.name || '';
        case 'sentAt':
          return item.sentAt?.getTime() || 0;
        case 'clickCount':
          return item.clickCount ?? -1;
        default:
          return (item as Record<string, any>)[property] ?? '';
      }
    };
  }

  async ngOnInit(): Promise<void> {
    await this.loadCampains();
    this.load();
    this.searchForm.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((value: string) => {
      this.filter = value;
      this.load();
      this.paginator.resetFirstPage();
    });
  }

  load(): void {
    const params: SentEmailFilterInterface = {
      page: this.page,
      limit: this.limit,
      filter: this.filter,
      status: this.status,
      campaignId: this.campaignId,
      channel: this.channel
    };

    this.marketingCampaignsHistoryService.getAll(params).then(response => {
      this.itemCount = response.count;
      this.tableDataSource.data = response.history.map(history => {
        const matchedCampaign = this.campaigns.find(campaign => campaign.id === history.campaignId);
        return {
          campaign: { name: matchedCampaign?.name, id: matchedCampaign?.id },
          target: history.to,
          channel: history.channel || 'email',
          sentAt: history.sentAt ? new Date(history.sentAt) : null,
          clickCount: history.clickCount,
          status: history.status,
          error: history.error,
          history
        };
      }).sort((a, b) => (b.sentAt?.getTime() || 0) - (a.sentAt?.getTime() || 0));
    });
    this.loadAnalytics(params);
  }

  private loadAnalytics(params: SentEmailFilterInterface): void {
    this.analyticsError = '';
    this.marketingCampaignsHistoryService.getAnalytics(params).then(response => {
      this.analytics = response.analytics;
      this.analyticsDataSource.data = response.analytics;
      this.analyticsTotals = response.totals;
    }).catch(() => {
      this.analytics = [];
      this.analyticsDataSource.data = [];
      this.analyticsTotals = this.emptyAnalyticsTotals();
      this.analyticsError = 'Campaign engagement analytics could not be loaded.';
    });
  }

  async loadCampains(): Promise<void> {
    await this.campaignService.getAll().then(campaigns => {
      this.campaigns = campaigns.map(campaign => ({ id: campaign.id, name: campaign.name }));
    });
  }

  setStatusFilter(event: MatSelectChange): void {
    this.status = event.value;
    this.load();
    this.paginator.resetFirstPage();
  }

  setCampaignFilter(event: MatSelectChange): void {
    this.campaignId = event.value;
    this.load();
    this.paginator.resetFirstPage();
  }

  setChannelFilter(event: MatSelectChange): void {
    this.channel = event.value;
    this.load();
    this.paginator.resetFirstPage();
  }

  selectCampaign(campaignId: string): void {
    this.campaignId = campaignId;
    this.load();
    this.paginator.resetFirstPage();
  }

  getStatusClass(status: string): string {
    return `status-${status || 'default'}`;
  }

  getChannelLabel(channel?: string): string {
    return channel === 'sms' ? 'SMS' : 'Email';
  }

  getCampaignName(campaignId: string): string {
    return this.campaigns.find(campaign => campaign.id === campaignId)?.name || 'Unknown campaign';
  }

  formatRate(rate: number | null): string {
    return rate == null ? '—' : `${rate.toFixed(1)}%`;
  }

  showDetails(item: MarketingCampaignHistoryRow): void {
    this.dialog.open(ObjectViewerComponent, {
      width: '720px',
      data: new ObjectViewerDialogModel(this.getHistoryDetails(item.history), 'Campaign History Details')
    });
  }

  private getHistoryDetails(history: SentEmailInterface): Record<string, unknown> {
    return {
      error: this.toDetailValue(history.error),
      to: this.toDetailValue(history.to),
      subject: this.toDetailValue(history.subject),
      body: this.toDetailValue(history.body),
      messageId: this.toDetailValue(history.messageId),
      sentAt: this.toDetailValue(history.sentAt),
      clickCount: this.toDetailValue(history.clickCount),
      clicks: this.formatClicks(history),
      bouncedAt: this.toDetailValue(history.bouncedAt),
      droppedAt: this.toDetailValue(history.droppedAt),
      spamAt: this.toDetailValue(history.spamAt),
      unsubscribedAt: this.toDetailValue(history.unsubscribedAt),
      lastEvent: this.toDetailValue(history.lastEvent),
      lastEventAt: this.toDetailValue(history.lastEventAt),
      status: this.toDetailValue(history.status)
    };
  }

  private toDetailValue(value: unknown): unknown {
    return value == null ? '' : value;
  }

  private formatClicks(history: SentEmailInterface): string {
    if (!history.clicks?.length) {
      return '';
    }

    return history.clicks
      .map(click => {
        const timestamp = this.formatDateValue(click.clickedAt);
        return click.originalUrl ? `${timestamp} - ${click.originalUrl}` : timestamp;
      })
      .join('\n');
  }

  private formatDateValue(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? `${value}` : date.toLocaleString();
  }

  pageEvent(event: any): void {
    this.limit = event.pageSize;
    this.page = event.pageIndex;
    this.load();
  }

  private emptyAnalyticsTotals(): MarketingCampaignAnalyticsTotals {
    return {
      attemptedCount: 0,
      sentCount: 0,
      deliveredCount: 0,
      failedCount: 0,
      uniqueClickedCount: 0,
      totalClickCount: 0,
      deliveryRate: null,
      uniqueClickRate: null,
      openRate: null
    };
  }
}
